import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Package,
  MapPin,
  Heart,
  Award,
  LogOut,
  Edit2,
  Plus,
  Trash2,
  ChevronRight,
} from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import {
  getMe,
  updateMe,
  getUserOrders,
  getAddresses,
  addAddress,
  deleteAddress,
  getLoyaltyStatus,
  getWishlist,
} from '../api/api';
import ProductCard from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to resolve image URLs (handles both local uploads and external URLs)
const resolveImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/60?text=No+Image';
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const AccountPage = () => {
  const navigate = useNavigate();
  const { language, user, isAuthenticated, logout, setUser, token } = useStore();
  const t = translations[language];

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [addressForm, setAddressForm] = useState({
    label: 'Home',
    address: '',
    city: '',
    postal_code: '',
    country: 'belgium',
    is_default: false,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAuthenticated, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'profile' && user) {
        setProfileForm({
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          phone: user.phone || '',
        });
      } else if (activeTab === 'orders') {
        const data = await getUserOrders();
        setOrders(data);
      } else if (activeTab === 'addresses') {
        const data = await getAddresses();
        setAddresses(data);
      } else if (activeTab === 'loyalty') {
        const data = await getLoyaltyStatus();
        setLoyalty(data);
      } else if (activeTab === 'wishlist') {
        const data = await getWishlist();
        setWishlist(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const updated = await updateMe(profileForm);
      setUser(updated, token);
      setEditingProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleAddAddress = async () => {
    try {
      const result = await addAddress(addressForm);
      setAddresses(result);
      setShowAddressForm(false);
      setAddressForm({
        label: 'Home',
        address: '',
        city: '',
        postal_code: '',
        country: 'belgium',
        is_default: false,
      });
    } catch (error) {
      console.error('Error adding address:', error);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await deleteAddress(addressId);
      setAddresses(addresses.filter((a) => a.id !== addressId));
    } catch (error) {
      console.error('Error deleting address:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  const levelColors = {
    bronze: 'from-amber-600 to-amber-800',
    silver: 'from-gray-400 to-gray-600',
    gold: 'from-yellow-400 to-yellow-600',
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-page">
      {/* Header */}
      <div className="bg-amber-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-white">{t.account.title}</h1>
          <p className="text-amber-200 mt-2">
            {language === 'fr' ? 'Bienvenue' : 'Welkom'}, {user?.first_name}!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow">
              {[
                { key: 'profile', icon: User, label: t.account.profile },
                { key: 'orders', icon: Package, label: t.account.orders },
                { key: 'addresses', icon: MapPin, label: t.account.addresses },
                { key: 'wishlist', icon: Heart, label: t.account.wishlist },
                { key: 'loyalty', icon: Award, label: t.account.loyalty },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => setActiveTab(item.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeTab === item.key
                      ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                {t.auth.logout}
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {loading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900">{t.account.profile}</h2>
                      {!editingProfile && (
                        <button
                          onClick={() => setEditingProfile(true)}
                          className="flex items-center gap-2 text-amber-700 hover:text-amber-800"
                        >
                          <Edit2 className="w-4 h-4" />
                          {t.account.editProfile}
                        </button>
                      )}
                    </div>

                    {editingProfile ? (
                      <div className="space-y-4 max-w-md">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.checkout.firstName}
                          </label>
                          <input
                            type="text"
                            value={profileForm.first_name}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, first_name: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.checkout.lastName}
                          </label>
                          <input
                            type="text"
                            value={profileForm.last_name}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, last_name: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.checkout.phone}
                          </label>
                          <input
                            type="tel"
                            value={profileForm.phone}
                            onChange={(e) =>
                              setProfileForm({ ...profileForm, phone: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div className="flex gap-4">
                          <button
                            onClick={handleUpdateProfile}
                            className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-lg"
                          >
                            {t.admin.save}
                          </button>
                          <button
                            onClick={() => setEditingProfile(false)}
                            className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50"
                          >
                            {t.admin.cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-sm text-gray-500">{t.auth.email}</dt>
                          <dd className="font-medium text-gray-900">{user?.email}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">
                            {t.checkout.firstName} {t.checkout.lastName}
                          </dt>
                          <dd className="font-medium text-gray-900">
                            {user?.first_name} {user?.last_name}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">{t.checkout.phone}</dt>
                          <dd className="font-medium text-gray-900">
                            {user?.phone || '-'}
                          </dd>
                        </div>
                      </dl>
                    )}
                  </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                      <h2 className="text-xl font-bold text-gray-900">{t.account.orders}</h2>
                    </div>
                    {orders.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{t.account.noOrders}</p>
                        <Link
                          to="/shop"
                          className="inline-block mt-4 text-amber-700 hover:text-amber-800"
                        >
                          {t.cart.continueShopping}
                        </Link>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {orders.map((order) => (
                          <div key={order.id} className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <p className="font-medium text-amber-700">
                                  {order.order_number}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {new Date(order.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  statusColors[order.status]
                                }`}
                              >
                                {t.admin.orderStatus[order.status]}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              {order.items?.slice(0, 3).map((item, idx) => (
                                item.image ? (
                                  <img
                                    key={idx}
                                    src={resolveImageUrl(item.image)}
                                    alt={item.name || ''}
                                    className="w-16 h-16 object-cover rounded border border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : (
                                  <div
                                    key={idx}
                                    className="w-16 h-16 bg-amber-50 rounded border border-amber-200 flex items-center justify-center"
                                    title={item.name || 'Produit'}
                                  >
                                    <Package className="w-6 h-6 text-amber-400" />
                                  </div>
                                )
                              ))}
                              {order.items?.length > 3 && (
                                <span className="text-sm text-gray-500">
                                  +{order.items.length - 3}
                                </span>
                              )}
                              <div className="ml-auto text-right">
                                <p className="font-bold text-amber-900">
                                  €{order.total?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Addresses Tab */}
                {activeTab === 'addresses' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b flex justify-between items-center">
                      <h2 className="text-xl font-bold text-gray-900">{t.account.addresses}</h2>
                      <button
                        onClick={() => setShowAddressForm(true)}
                        className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg"
                      >
                        <Plus className="w-4 h-4" />
                        {t.account.addAddress}
                      </button>
                    </div>

                    {showAddressForm && (
                      <div className="p-6 border-b bg-gray-50">
                        <h3 className="font-medium mb-4">{t.account.addAddress}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Label
                            </label>
                            <select
                              value={addressForm.label}
                              onChange={(e) =>
                                setAddressForm({ ...addressForm, label: e.target.value })
                              }
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="Home">Home</option>
                              <option value="Work">Work</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.address}
                            </label>
                            <input
                              type="text"
                              value={addressForm.address}
                              onChange={(e) =>
                                setAddressForm({ ...addressForm, address: e.target.value })
                              }
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.city}
                            </label>
                            <input
                              type="text"
                              value={addressForm.city}
                              onChange={(e) =>
                                setAddressForm({ ...addressForm, city: e.target.value })
                              }
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.postalCode}
                            </label>
                            <input
                              type="text"
                              value={addressForm.postal_code}
                              onChange={(e) =>
                                setAddressForm({ ...addressForm, postal_code: e.target.value })
                              }
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.country}
                            </label>
                            <select
                              value={addressForm.country}
                              onChange={(e) =>
                                setAddressForm({ ...addressForm, country: e.target.value })
                              }
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="belgium">{t.checkout.belgium}</option>
                              <option value="france">{t.checkout.france}</option>
                              <option value="netherlands">{t.checkout.netherlands}</option>
                            </select>
                          </div>
                          <div className="flex items-center">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={addressForm.is_default}
                                onChange={(e) =>
                                  setAddressForm({ ...addressForm, is_default: e.target.checked })
                                }
                                className="w-4 h-4 text-amber-600 rounded"
                              />
                              <span className="text-sm">{t.account.defaultAddress}</span>
                            </label>
                          </div>
                        </div>
                        <div className="flex gap-4 mt-4">
                          <button
                            onClick={handleAddAddress}
                            className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-lg"
                          >
                            {t.admin.save}
                          </button>
                          <button
                            onClick={() => setShowAddressForm(false)}
                            className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50"
                          >
                            {t.admin.cancel}
                          </button>
                        </div>
                      </div>
                    )}

                    {addresses.length === 0 && !showAddressForm ? (
                      <div className="p-8 text-center text-gray-500">
                        <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{language === 'fr' ? 'Aucune adresse enregistrée' : 'Geen adressen opgeslagen'}</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {addresses.map((addr) => (
                          <div key={addr.id} className="p-6 flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">{addr.label}</span>
                                {addr.is_default && (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                    {t.account.defaultAddress}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600">{addr.address}</p>
                              <p className="text-gray-600">
                                {addr.postal_code} {addr.city}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteAddress(addr.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Wishlist Tab */}
                {activeTab === 'wishlist' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                      <h2 className="text-xl font-bold text-gray-900">{t.account.wishlist}</h2>
                    </div>
                    {wishlist.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Heart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>{t.wishlist.empty}</p>
                        <Link
                          to="/shop"
                          className="inline-block mt-4 text-amber-700 hover:text-amber-800"
                        >
                          {t.cart.continueShopping}
                        </Link>
                      </div>
                    ) : (
                      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {wishlist.map((product) => (
                          <ProductCard key={product.id} product={product} />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Loyalty Tab */}
                {activeTab === 'loyalty' && loyalty && (
                  <div className="space-y-6">
                    {/* Loyalty Card */}
                    <div
                      className={`bg-gradient-to-br ${levelColors[loyalty.level]} rounded-lg shadow-lg p-8 text-white`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white/80 text-sm">{t.loyalty.level}</p>
                          <h2 className="text-3xl font-bold capitalize">
                            {t.loyalty[loyalty.level]}
                          </h2>
                        </div>
                        <Award className="w-12 h-12 text-white/80" />
                      </div>
                      <div className="mt-8">
                        <p className="text-white/80 text-sm">{t.loyalty.points}</p>
                        <p className="text-4xl font-bold">{loyalty.points}</p>
                      </div>
                      {loyalty.next_level && (
                        <div className="mt-6">
                          <div className="flex justify-between text-sm mb-2">
                            <span>{t.loyalty.pointsToNext}</span>
                            <span>{loyalty.points_to_next_level}</span>
                          </div>
                          <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-white rounded-full"
                              style={{
                                width: `${Math.min(
                                  100,
                                  (loyalty.points /
                                    (loyalty.points + loyalty.points_to_next_level)) *
                                    100
                                )}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Benefits */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">
                        {t.loyalty.benefits}
                      </h3>
                      <div className="space-y-4">
                        {Object.entries(loyalty.levels).map(([level, data]) => (
                          <div
                            key={level}
                            className={`p-4 rounded-lg border-2 ${
                              loyalty.level === level
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <h4 className="font-semibold capitalize">
                                  {t.loyalty[level]}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {data.min_points}+ {t.loyalty.points.toLowerCase()}
                                </p>
                              </div>
                              <span className="text-amber-700 font-bold">
                                {data.discount}% {t.loyalty.discount.toLowerCase()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-6 text-sm text-gray-500">
                        💡 {t.loyalty.earnPoints}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
